<template>
  <div class="svg-preview">
    <label v-if="label" class="svg-preview__label">{{ label }}</label>
    <div class="svg-preview__area" :class="{ 'svg-preview__area--empty': !src }">
      <template v-if="src">
        <div class="svg-preview__wrap">
          <img
            :src="src"
            :alt="alt"
            class="svg-preview__img"
          />
          <v-btn
            icon
            class="svg-preview__download"
            :title="downloadTitle"
            @click="download"
          >
            <v-icon color="white">mdi-download-outline</v-icon>
          </v-btn>
        </div>
      </template>
      <div v-else class="svg-preview__placeholder">
        <div class="svg-preview__placeholder-icon-wrap">
          <v-icon size="36" class="svg-preview__placeholder-icon">mdi-image-outline</v-icon>
        </div>
        <p class="svg-preview__placeholder-text">{{ placeholderText }}</p>
        <p v-if="placeholderHint" class="svg-preview__placeholder-hint">{{ placeholderHint }}</p>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SvgPreviewWithDownload',
  props: {
    label: { type: String, default: '' },
    /** SVG image URL (or null for empty state). */
    src: { type: String, default: null },
    /** Filename used when downloading (e.g. "order123_back.svg"). */
    downloadFilename: { type: String, default: 'download.svg' },
    alt: { type: String, default: 'Preview' },
    downloadTitle: { type: String, default: 'Download SVG' },
    placeholderText: { type: String, default: 'Preview will appear here' },
    placeholderHint: { type: String, default: '' },
    /** Width of the white preview box in px (default 256). */
    previewWidth: { type: Number, default: 256 },
  },
  methods: {
    async download() {
      if (!this.src) return
      try {
        const response = await fetch(this.src)
        const blob = await response.blob()
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = this.downloadFilename || 'download.svg'
        link.click()
        URL.revokeObjectURL(url)
        this.$emit('download')
      } catch (err) {
        console.error('Download failed:', err)
        this.$emit('download-error', err)
      }
    },
  },
}
</script>

<style scoped>
.svg-preview__label {
  display: block;
  font-size: 0.65rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.45);
  margin-bottom: 6px;
  cursor: default;
}

.svg-preview__area {
  position: relative;
  min-height: 240px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px;
  overflow: hidden;
  transition: background 0.2s ease, border-color 0.2s ease;
}

.svg-preview__area--empty {
  background: rgba(255, 255, 255, 0.02);
  border: 2px dashed rgba(255, 255, 255, 0.1);
}

.svg-preview__wrap {
  position: relative;
  width: 256px;
  max-width: 100%;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 0;
}

.svg-preview__wrap .svg-preview__img {
  width: 256px;
  height: auto;
  max-height: 320px;
  object-fit: contain;
  display: block;
}

.svg-preview__download {
  position: absolute !important;
  top: 6px;
  right: 6px;
  z-index: 2;
  margin: 0 !important;
  min-width: 32px !important;
  width: 32px !important;
  height: 32px !important;
  min-height: 32px !important;
  background: rgba(0, 0, 0, 0.55) !important;
  border-radius: 6px !important;
  transition: background 0.2s ease, transform 0.15s ease;
}

.svg-preview__download .v-icon {
  font-size: 18px !important;
}

.svg-preview__download:hover {
  background: rgba(0, 0, 0, 0.75) !important;
  transform: scale(1.06);
}

.svg-preview__placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-align: center;
  padding: 16px;
}

.svg-preview__placeholder-icon-wrap {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  align-items: center;
  justify-content: center;
}

.svg-preview__placeholder-icon {
  color: rgba(255, 255, 255, 0.2) !important;
}

.svg-preview__placeholder-text {
  font-size: 0.8125rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.5);
  margin: 0;
}

.svg-preview__placeholder-hint {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.35);
  margin: 0;
}
</style>
